import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  CalendarDays, Clock, Car, MapPin,
  Send, User, CheckCircle2, Loader2, AlertCircle,
} from 'lucide-react';

// ── Stat pill ──────────────────────────────────────────────────────────────────

function StatPill({ icon: Icon, label, value, muted }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border ${muted ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}>
      <Icon className={`h-4 w-4 shrink-0 ${muted ? 'text-gray-400' : 'text-gray-500'}`} />
      <div>
        <p className={`text-xs font-semibold leading-none ${muted ? 'text-gray-400' : 'text-gray-900'}`}>
          {value}
        </p>
        <p className="text-[10px] text-gray-400 mt-0.5 leading-none">{label}</p>
      </div>
    </div>
  );
}

// ── Read-only job row ──────────────────────────────────────────────────────────

function JobRow({ job, index }) {
  return (
    <div className="flex items-start gap-2.5 py-2.5" data-testid={`reminder-job-${job.taskId}`}>
      <div className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-semibold text-gray-900 truncate">{job.clientName}</span>
          {job.alreadySent && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
              <CheckCircle2 className="h-2.5 w-2.5" />
              Notified
            </span>
          )}
        </div>
        {job.timeLabel && (
          <div className="flex items-center gap-1 mt-0.5">
            <Clock className="h-3 w-3 text-gray-400 shrink-0" />
            <span className="text-xs text-gray-500">{job.timeLabel}</span>
          </div>
        )}
        {job.address && (
          <div className="flex items-center gap-1 mt-0.5">
            <MapPin className="h-3 w-3 text-gray-400 shrink-0" />
            <span className="text-xs text-gray-400 truncate">{job.address}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tech section ───────────────────────────────────────────────────────────────

function TechSection({ name, jobs, showHeader }) {
  return (
    <div>
      {showHeader && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <User className="h-3 w-3 text-gray-400" />
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
            {name}
          </p>
        </div>
      )}
      <div className="border border-gray-100 rounded-xl divide-y divide-gray-100 px-3">
        {jobs.map((job, i) => (
          <JobRow key={job.taskId} job={job} index={i} />
        ))}
      </div>
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────────

export default function SendRemindersModal({
  open,
  onClose,
  preview,        // { jobCount, jobs, serviceWindow, driveLabel, alreadySentCount }
  loading,
  onConfirmSend,
  sending,
  dispatchDate,   // Date object — the currently selected Dispatch date
}) {
  const {
    jobCount = 0, jobs = [], serviceWindow,
    driveLabel, alreadySentCount = 0,
  } = preview || {};
  const pendingCount = jobCount - alreadySentCount;

  const dateLabel = dispatchDate instanceof Date
    ? dispatchDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
    : '';

  // Group jobs by technician for clearer confirmation
  const jobsByTech = useMemo(() => {
    if (!jobs.length) return [];
    const groups = {};
    const order = [];
    jobs.forEach((job) => {
      const key = job.techName || 'Unassigned';
      if (!groups[key]) { groups[key] = []; order.push(key); }
      groups[key].push(job);
    });
    return order.map((name) => ({ name, jobs: groups[name] }));
  }, [jobs]);

  const multiTech = jobsByTech.length > 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md p-0 gap-0 overflow-hidden flex flex-col"
        style={{ maxHeight: '85vh' }}
        data-testid="send-reminders-modal"
      >
        {/* ── Fixed header ───────────────────────────────────────────────── */}
        <DialogHeader className="px-5 pt-5 pb-3 shrink-0 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-indigo-50 rounded-lg">
              <CalendarDays className="h-4 w-4 text-indigo-600" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-gray-900">
                Send Reminders
              </DialogTitle>
              <p className="text-xs text-gray-500 font-normal">{dateLabel}</p>
            </div>
          </div>
        </DialogHeader>

        {/* ── Scrollable body ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 pt-4 pb-2 space-y-4 min-h-0">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 text-indigo-400 animate-spin" />
            </div>
          )}

          {!loading && jobCount === 0 && (
            <div className="text-center py-8">
              <AlertCircle className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-600 font-medium">No jobs scheduled on this date</p>
              <p className="text-xs text-gray-400 mt-1">Nothing to preview or notify.</p>
            </div>
          )}

          {!loading && jobCount > 0 && (
            <>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-2">
                <StatPill icon={CalendarDays} label="jobs" value={jobCount} />
                {serviceWindow?.startLabel ? (
                  <StatPill
                    icon={Clock}
                    label="service window"
                    value={serviceWindow.endLabel
                      ? `${serviceWindow.startLabel} – ${serviceWindow.endLabel}`
                      : `from ${serviceWindow.startLabel}`}
                  />
                ) : (
                  <StatPill icon={Clock} label="service window" value="TBD" muted />
                )}
                {driveLabel
                  ? <StatPill icon={Car} label="est. drive" value={driveLabel} />
                  : <StatPill icon={Car} label="est. drive" value="N/A" muted />
                }
              </div>

              {/* Section label */}
              <div>
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Scheduled Jobs
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  Order reflects current dispatch sequence. Adjust in the board before sending.
                </p>
              </div>

              {/* Job list grouped by technician */}
              <div className="space-y-3">
                {jobsByTech.map(({ name, jobs: techJobs }) => (
                  <TechSection
                    key={name}
                    name={name}
                    jobs={techJobs}
                    showHeader={multiTech}
                  />
                ))}
              </div>

              {alreadySentCount > 0 && (
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                  {alreadySentCount === jobCount
                    ? 'All customers already notified.'
                    : `${alreadySentCount} already notified — ${pendingCount} pending.`}
                </p>
              )}
            </>
          )}
        </div>

        {/* ── Fixed footer ─────────────────────────────────────────────────── */}
        <div className="shrink-0 px-5 pt-3 pb-5 border-t border-gray-100 space-y-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-10 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              data-testid="reminders-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirmSend}
              disabled={sending || loading || jobCount === 0 || pendingCount === 0}
              className="flex-1 h-10 rounded-xl bg-indigo-600 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              data-testid="reminders-send-btn"
            >
              {sending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Send className="h-4 w-4" />
              }
              {sending
                ? 'Sending…'
                : pendingCount === 0 && jobCount === 0
                  ? 'No jobs'
                  : pendingCount === 0
                    ? 'All notified'
                    : `Send ${pendingCount} reminder${pendingCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
